import { Box, Button, Flex, Stack, Textarea, useDisclosure, useTheme, Input, Select } from '@chakra-ui/react'
import { useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import Heading from 'components/heading'
import DeleteStreamAlert from 'components/delete-stream-alert'
import style1 from 'assets/style1.jpeg'
import style2 from 'assets/style2.jpeg'
import style3 from 'assets/style3.png'
import style4 from 'assets/style4.png'
import sampleAudio from 'assets/audio-preview.mp3'
import sampleAudioHindi from 'assets/audio-preview-hindi.mp3'

export default function CreateVideo() {
  const theme = useTheme()
  const { isOpen: deleteModalIsOpen, onOpen: deleteModalOnOpen, onClose: deleteModalOnClose } = useDisclosure()
  const [isProcessing, setIsProcessing] = useState(false)
  const [streamDeleting, setStreamDeleting] = useState(null)
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [selectedTone, setSelectedTone] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('english')

  const navigate = useNavigate()
  const location = useLocation()

  const audioRef = useRef(null)
  const [videoName, setVideoName] = useState('IPL Campaign')

  // Languages ordered by approximate global usage/speakers
  const languages = [
    { code: 'english', name: 'English' },
    { code: 'hindi', name: 'Hindi' },
    { code: 'mandarin', name: 'Mandarin Chinese' },
    { code: 'spanish', name: 'Spanish' },
    { code: 'arabic', name: 'Arabic' },
    { code: 'french', name: 'French' },
    { code: 'bengali', name: 'Bengali' },
    { code: 'portuguese', name: 'Portuguese' },
    { code: 'russian', name: 'Russian' },
    { code: 'urdu', name: 'Urdu' },
    { code: 'indonesian', name: 'Indonesian' },
    { code: 'german', name: 'German' },
    { code: 'japanese', name: 'Japanese' },
    { code: 'swahili', name: 'Swahili' },
    { code: 'marathi', name: 'Marathi' },
    { code: 'telugu', name: 'Telugu' },
    { code: 'turkish', name: 'Turkish' },
    { code: 'tamil', name: 'Tamil' },
    { code: 'vietnamese', name: 'Vietnamese' },
    { code: 'italian', name: 'Italian' },
    { code: 'thai', name: 'Thai' },
    { code: 'persian', name: 'Persian' },
    { code: 'gujarati', name: 'Gujarati' },
    { code: 'polish', name: 'Polish' },
    { code: 'punjabi', name: 'Punjabi' },
    { code: 'korean', name: 'Korean' },
    { code: 'kannada', name: 'Kannada' },
    { code: 'malayalam', name: 'Malayalam' },
    { code: 'dutch', name: 'Dutch' },
    { code: 'romanian', name: 'Romanian' },
    { code: 'greek', name: 'Greek' },
    { code: 'nepali', name: 'Nepali' }
  ]

  const [scriptInputs, setScriptInputs] = useState([
    'Ladies and gents, welcome to another electrifying season of IPL!',
    "I'm Pat Cummins, and I'm thrilled to be part of the action, where the best in cricket come together.",
    'So strap yourselves in for some big hits, fast bowling, and unforgettable moments!',
  ])

  const addScriptInput = () => setScriptInputs([...scriptInputs, ''])

  const updateScriptInput = (index, value) => {
    const updatedScripts = [...scriptInputs]
    updatedScripts[index] = value
    setScriptInputs(updatedScripts)
  }

  const removeLastScriptInput = () => {
    if (scriptInputs.length > 3) {
      setScriptInputs(scriptInputs.slice(0, -1))
    }
  }

  const handleAudioToggle = () => {
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.currentTime = 0
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const { imageSrc } = location.state || {}

  return (
    <>
      <Flex w="100%" h="100%" mb={8} direction="column" align="center">
        <Stack
          w="100%"
          direction="column"
          align="center"
          spacing={4}
          mx={{ base: 0, xl: 16 }}
          maxW={{ base: '100%', xl: '860px' }}
        >
          <Flex
            w="100%"
            borderRadius="5px"
            px={{ base: 4, lg: 0 }}
            py={{ base: 4, lg: 2 }}
            backgroundColor={theme.colors.gray[50]}
          >
            <Heading as="h1" text="Create Video" />
          </Flex>

          {/* Video Name Input */}
          <Flex w="100%" mb={4} px={{ base: 4, lg: 0 }} py={{ base: 4, lg: 2 }}>
            <Input
              placeholder="Enter video name"
              value={videoName}
              onChange={e => setVideoName(e.target.value)}
              size="lg"
              borderRadius="5px"
              backgroundColor="white"
            />
          </Flex>

          {/* Style, Tone, and Language Selection */}
          <Flex w="100%" direction={{ base: 'column', md: 'row' }} gap={8} px={{ base: 4, lg: 0 }}>
            <Box>
              <Heading as="h2" fontSize="sm" mb={2} text="Alternate Style"></Heading>
              <Flex direction="row" gap={2} mb={4}>
                {[style1, style2, style3, style4].map((style, index) => (
                  <Box
                    key={index}
                    as="button"
                    w="50px"
                    h="50px"
                    bg="gray.300"
                    borderRadius="md"
                    onClick={() => setSelectedStyle(style)}
                    overflow="hidden"
                    border={selectedStyle === style ? `2px solid ${theme.colors.purple[600]}` : 'none'}
                  >
                    <img
                      src={style}
                      alt={`Style ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Flex>
            </Box>

            <Box>
              <Heading as="h2" fontSize="sm" mb={2} text="Select Tone"></Heading>
              <Flex direction="row" gap={2} mb={4}>
                {[
                  { tone: 'excited', emoji: '🤩' },
                  { tone: 'happy', emoji: '😊' },
                  { tone: 'neutral', emoji: '😐' },
                  { tone: 'sad', emoji: '😢' },
                ].map(({ tone, emoji }) => (
                  <Box
                    key={tone}
                    as="button"
                    w="50px"
                    h="50px"
                    bg="gray.300"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="24px"
                    onClick={() => setSelectedTone(tone)}
                    border={selectedTone === tone ? `2px solid ${theme.colors.purple[600]}` : 'none'}
                  >
                    {emoji}
                  </Box>
                ))}
              </Flex>
            </Box>

            <Box>
              <Heading as="h2" fontSize="sm" mb={2} text="Select Language"></Heading>
              <Select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                w="50px"
                h="50px"
                borderRadius="5px"
                backgroundColor="white"
                width="200px"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>

          {/* Main Content Area */}
          <Flex w="100%" mt={8} gap={8} direction={{ base: 'column', md: 'row' }} px={{ base: 4, lg: 0 }}>
            {/* Script Inputs */}
            <Box w={{ base: '100%', md: '50%' }}>
              <Heading as="h2" fontSize="sm" mb={2} text="Script"></Heading>
              {scriptInputs.map((input, index) => (
                <Flex key={index} align="center" mb={4}>
                  <Textarea
                    value={input}
                    onChange={e => updateScriptInput(index, e.target.value)}
                    size="xl"
                    resize="vertical"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  />
                </Flex>
              ))}
              <Flex mt={2} gap={4} justifyContent="center">
                <Button
                  onClick={removeLastScriptInput}
                  size="sm"
                  disabled={scriptInputs.length <= 3}
                >
                  -
                </Button>
                <Button onClick={addScriptInput} size="sm">
                  +
                </Button>
              </Flex>
            </Box>

            {/* Preview */}
            <Box w={{ base: '100%', md: '50%' }}>
              <Heading as="h2" fontSize="sm" mb={2} text="Preview"></Heading>
              <Flex
                w="100%"
                h="300px"
                bg="gray.100"
                justifyContent="center"
                alignItems="center"
                borderRadius="md"
                overflow="hidden"
              >
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt="Preview Image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : selectedStyle ? (
                  <img
                    src={selectedStyle}
                    alt="Selected Style"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <p>Select a style or generate an image</p>
                )}
              </Flex>
              <Flex gap={4} mt={4}>
                <Button
                  color="white"
                  bg={theme.baseColors.secondary.main}
                  w="50%"
                  onClick={handleAudioToggle}
                  _hover={{ bg: theme.baseColors.secondary.light }}
                >
                  {isPlaying ? 'Stop' : 'Preview'}
                </Button>
                <Button
                  color="white"
                  bg={theme.baseColors.primary.main}
                  w="50%"
                  _hover={{ bg: theme.baseColors.secondary.light }}
                  onClick={() => navigate('/manage-video')}
                >
                  Process Video
                </Button>
              </Flex>
              <audio
                ref={audioRef}
                src={selectedLanguage === 'hindi' ? sampleAudioHindi : sampleAudio}
                onEnded={() => setIsPlaying(false)}
              />
            </Box>
          </Flex>
        </Stack>
      </Flex>

      <DeleteStreamAlert
        isOpen={deleteModalIsOpen}
        onClose={() => {
          deleteModalOnClose()
          setStreamDeleting(null)
        }}
        isLoading={isProcessing}
        setIsLoading={setIsProcessing}
        streamDeleting={streamDeleting}
        setStreamDeleting={setStreamDeleting}
        isOrphan
      />
    </>
  )
}